/* eslint-disable no-restricted-syntax */

import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {
  currentCohortPathSelector,
  currentUserIdSelector,
} from '../Auth/selectors';
import { selectCurrentCoursePath } from '../Courses/selectors';
import { currentUserProfileNameSelector } from '../Users/selectors';

const cohortsSelector = (state) => state.Cohorts;

export const enrolmentsSelector = (state) => state.Enrolments;

const usersSelector = (state) => state.Users;

const coursesSelector = (state) => state.Courses;

export const enrolmentsByCohortSelector = createSelector(
  enrolmentsSelector,
  cohortsSelector,
  (enrolments, cohorts) => {
    let byCohort = Immutable.Map();

    for (const enrolment of enrolments.values()) {
      byCohort = byCohort.setIn(
        [enrolment.get('cohortPath'), 'byStudent', enrolment.get('userId')],
        enrolment
      );
    }

    for (const cohort of cohorts.values()) {
      if (!byCohort.has(cohort.get('path'))) {
        byCohort = byCohort.setIn(
          [cohort.get('path'), 'byStudent'],
          Immutable.Map()
        );
      }
    }

    return byCohort;
  }
);

export const enrolmentsByUserIdSelector = createSelector(
  enrolmentsSelector,
  (enrolments) => {
    let byUserId = Immutable.Map();

    for (const enrolment of enrolments.values()) {
      byUserId = byUserId.setIn(
        [
          enrolment.get('userId'),
          'byCoursePath',
          enrolment.get('coursePath'),
          enrolment.get('id'),
        ],
        enrolment
      );
    }

    return byUserId;
  }
);

export const currentUserEnrolmentsByCourseSelector = createSelector(
  currentUserIdSelector,
  enrolmentsByUserIdSelector,
  (currentUserId, enrolmentsByUserId) => {
    return currentUserId
      ? enrolmentsByUserId.getIn(
          [currentUserId, 'byCoursePath'],
          Immutable.Map()
        )
      : Immutable.Map();
  }
);

export const currentUserEnrolmentsSortedByCourseCohortSelector = createSelector(
  currentUserEnrolmentsByCourseSelector,
  coursesSelector,
  cohortsSelector,
  (enrolmentsByCoursePath, courses, cohorts) => {
    return (
      enrolmentsByCoursePath
        // crunch it down to a list of enrolments
        .reduce((accumulator, courseEnrolments) => {
          let result = accumulator;

          courseEnrolments.forEach((e) => (result = result.push(e)));

          return result;
        }, Immutable.List())
        .sort((a, b) => {
          // sort by course name, then cohort name
          const courseNames = {
            a: courses.get(a.get('coursePath')).get('name').toLowerCase(),
            b: courses.get(b.get('coursePath')).get('name').toLowerCase(),
          };
          const courseSort = courseNames.a.localeCompare(courseNames.b);

          if (courseSort !== 0) {
            return courseSort;
          }

          const cohortNames = {
            a: cohorts.get(a.get('cohortPath')).get('title').toLowerCase(),
            b: cohorts.get(b.get('cohortPath')).get('title').toLowerCase(),
          };

          return cohortNames.a.localeCompare(cohortNames.b);
        })
    );
  }
);

export const currentCohortEnrolmentsByStudentSelector = createSelector(
  enrolmentsByCohortSelector,
  currentCohortPathSelector,
  (enrolmentsByCohort, currentCohort) => {
    return enrolmentsByCohort.getIn(
      [currentCohort, 'byStudent'],
      Immutable.Map()
    );
  }
);

export const currentCohortEnrolmentsSortedByStudentFullName = createSelector(
  currentCohortEnrolmentsByStudentSelector,
  usersSelector,
  (enrolmentsByStudent, users) => {
    return enrolmentsByStudent.toList().sortBy((enrolment) => {
      return users
        .getIn([enrolment.get('userId'), 'fullName'], '')
        .toLowerCase();
    });
  }
);

export const selectIsFacilitatorInCurrentCourse = createSelector(
  currentUserEnrolmentsByCourseSelector,
  selectCurrentCoursePath,
  (currentUserEnrolmentsByCourse, currentCourse) => {
    return currentUserEnrolmentsByCourse
      .get(currentCourse, new Immutable.List())
      .some((enrolment) => {
        return enrolment.get('staff');
      });
  }
);

export const selectIsFacilitatorEnrolments = createSelector(
  enrolmentsSelector,
  currentUserIdSelector,
  (enrolments, currentUserId) => {
    return currentUserId
      ? enrolments.filter((enrolment) => {
          return (
            enrolment.get('staff') && enrolment.get('userId') === currentUserId
          );
        })
      : Immutable.Map();
  }
);

export const selectCurrentUserAsFacilitatorByCourses = createSelector(
  selectIsFacilitatorEnrolments,
  (enrolments) => {
    let byCourses = Immutable.Map();

    for (const enrolment of enrolments.values()) {
      byCourses = byCourses.setIn(
        ['byCourses', enrolment.get('coursePath'), enrolment.get('id')],
        enrolment
      );
    }
    return byCourses;
  }
);

export const selectCoursesAsFacilitatorByInstitutions = createSelector(
  coursesSelector,
  selectCurrentUserAsFacilitatorByCourses,
  (courses, enrolments) => {
    let byInstitutions = Immutable.Map();

    const filteredCourses = courses.filter((course) => {
      return enrolments.getIn(['byCourses', course.get('path')]);
    });

    for (const course of filteredCourses.values()) {
      byInstitutions = byInstitutions.setIn(
        [
          'byInstitutions',
          course.get('institution', 'default'),
          course.get('path'),
        ],
        course
      );
    }

    return byInstitutions;
  }
);

export const selectEnrolmentsOfCurrentUser = createSelector(
  enrolmentsSelector,
  currentUserIdSelector,
  (enrolments, currentUserId) => {
    return currentUserId
      ? enrolments.filter((enrolment) => {
          return enrolment.get('userId') === currentUserId;
        })
      : Immutable.Map();
  }
);

export const selectEnrolmentsOfCurrentUserJS = createSelector(
  selectEnrolmentsOfCurrentUser,
  (enrolments) => enrolments.toJS()
);

export const selectCurrentUserAsFacilitatorOrAdmin = createSelector(
  coursesSelector,
  selectEnrolmentsOfCurrentUser,
  currentUserProfileNameSelector,
  (courses, enrolments, currentUserProfileName) => {
    return currentUserProfileName
      ? enrolments.filter((enrolment) => {
          return (
            enrolment.get('staff') ||
            courses
              .getIn([enrolment.get('coursePath'), 'admins'])
              .indexOf(currentUserProfileName) !== -1
          );
        })
      : Immutable.Map();
  }
);

export const selectCurrentUserAsFacilitatorOrAdminByCourses = createSelector(
  selectCurrentUserAsFacilitatorOrAdmin,
  (enrolments) => {
    let byCourses = Immutable.Map();
    for (const enrolment of enrolments.values()) {
      byCourses = byCourses.setIn(
        ['byCourses', enrolment.get('coursePath'), enrolment.get('id')],
        enrolment
      );
    }

    return byCourses;
  }
);

export const selectAsFacilitatorOrAdminByInstitutions = createSelector(
  coursesSelector,
  selectCurrentUserAsFacilitatorOrAdminByCourses,
  (courses, enrolments) => {
    let byInstitutions = Immutable.Map();

    const filteredCourses = courses.filter((course) => {
      return enrolments.getIn(['byCourses', course.get('path')]);
    });

    for (const course of filteredCourses.values()) {
      byInstitutions = byInstitutions.setIn(
        [
          'byInstitutions',
          course.get('institution', 'default'),
          course.get('path'),
        ],
        course
      );
    }

    return byInstitutions;
  }
);

export const selectEnrolmentForCurrentCourse = createSelector(
  enrolmentsSelector,
  selectCurrentCoursePath,
  (enrolments, coursePath) => {
    return enrolments
      .filter((enrolment) => enrolment.get('coursePath') === coursePath)
      .first();
  }
);

export const selectIsEnrolmentConsentRequired = createSelector(
  selectEnrolmentForCurrentCourse,
  (enrolment) => {
    const hasEnrolmentConsentPermission = enrolment?.get(
      'hasEnrolmentConsentPermission'
    );
    return hasEnrolmentConsentPermission === false;
  }
);
